import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { SignOut, ArrowLeft, PencilSimple, Check, X } from '@phosphor-icons/react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';

import { Button, Spinner, EmptyState } from '@/components/ui';
import { UserAvatar } from '@/components/features/UserAvatar/UserAvatar';
import { PollCard } from '@/components/features/PollCard/PollCard';
import { PollCardSkeleton } from '@/components/features/PollCardSkeleton/PollCardSkeleton';
import { useAuthStore } from '@/stores';
import { usePolls } from '@/api';
import { ROUTES } from '@/constants';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import { sanitizeName } from '@/lib/sanitize';

import styles from './Profile.module.css';

export function Profile() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { isLogged, getUser, logOut, setUser } = useAuthStore();
    const user = getUser();
    const isOwnProfile = user && slug && user.slug === slug;
    const [isEditingName, setIsEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [savingName, setSavingName] = useState(false);

    const {
        data,
        isLoading,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = usePolls({ mine: isOwnProfile || undefined, ordering: '-created' });
    const polls = data?.pages.flatMap((page) => page.results) ?? [];

    const { ref: loadMoreRef } = useInView({
        onChange: (inView) => {
            if (inView && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
    });

    const displayName = isOwnProfile ? (user?.display_name || 'Usuario') : `@${slug}`;

    useEffect(() => {
        document.title = `${displayName} — Ratio`;
    }, [displayName]);

    const handleLogout = async () => {
        await logOut();
        navigate(ROUTES.login);
    };

    const handleStartEditName = () => {
        setEditName(user?.display_name || '');
        setIsEditingName(true);
    };

    const handleSaveName = async () => {
        const sanitized = sanitizeName(editName);
        if (!sanitized || sanitized.length < 2) {
            toast.error('El nombre debe tener al menos 2 caracteres');
            return;
        }
        setSavingName(true);
        try {
            const { data } = await api.patch(API_ROUTES.user, { display_name: sanitized });
            setUser(data);
            setIsEditingName(false);
            toast.success('Perfil actualizado');
        } catch {
            toast.error('No se pudo actualizar el perfil');
        } finally {
            setSavingName(false);
        }
    };

    const handleCancelEditName = () => {
        setIsEditingName(false);
        setEditName('');
    };

    if (!user && isOwnProfile) {
        return (
            <div className={styles.error}>
                <p>No se pudo cargar el perfil</p>
                <Button onClick={() => navigate(ROUTES.login)}>Iniciar sesion</Button>
            </div>
        );
    }

    const avatarUrl = isOwnProfile ? user?.avatar_url || null : null;
    const email = isOwnProfile ? user?.email : undefined;

    return (
        <div className={styles.page}>
            <button
                className={styles.backBtn}
                onClick={() => navigate(-1)}
                aria-label="Volver"
            >
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.profileHeader}>
                <UserAvatar
                    src={avatarUrl}
                    name={displayName}
                    size="lg"
                />
                <div className={styles.profileInfo}>
                    {isEditingName ? (
                        <div className={styles.editNameRow}>
                            <input
                                className={styles.editNameInput}
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                autoFocus
                                maxLength={50}
                            />
                            <button
                                className={styles.editNameBtn}
                                onClick={handleSaveName}
                                disabled={savingName}
                                aria-label="Guardar nombre"
                            >
                                {savingName ? <Spinner size="sm" /> : <Check size={16} />}
                            </button>
                            <button
                                className={styles.editNameBtn}
                                onClick={handleCancelEditName}
                                aria-label="Cancelar edicion"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ) : (
                        <div className={styles.nameRow}>
                            <h1 className={styles.name}>{displayName}</h1>
                            {isOwnProfile && (
                                <button
                                    className={styles.editNameBtn}
                                    onClick={handleStartEditName}
                                    aria-label="Editar nombre"
                                >
                                    <PencilSimple size={14} />
                                </button>
                            )}
                        </div>
                    )}
                    {email && <p className={styles.email}>{email}</p>}
                </div>
                {isOwnProfile && isLogged && (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleLogout}
                    >
                        <SignOut size={16} />
                        Cerrar sesion
                    </Button>
                )}
            </div>

            <h2 className={styles.sectionTitle}>
                {isOwnProfile ? 'Mis encuestas' : 'Encuestas'}
            </h2>

            {isLoading ? (
                <div className={styles.list}>
                    {[1, 2, 3].map((i) => (
                        <PollCardSkeleton key={i} />
                    ))}
                </div>
            ) : polls.length === 0 ? (
                <EmptyState
                    title="Sin encuestas"
                    description={isOwnProfile ? 'Aun no has creado encuestas' : 'Este usuario no ha creado encuestas'}
                    actionLabel={isOwnProfile ? 'Crear encuesta' : undefined}
                    onAction={isOwnProfile ? () => navigate(ROUTES.create) : undefined}
                />
            ) : (
                <>
                    <div className={styles.list}>
                        {polls.map((poll) => (
                            <motion.div
                                key={poll.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <PollCard poll={poll} />
                            </motion.div>
                        ))}
                    </div>

                    {hasNextPage && (
                        <div ref={loadMoreRef} className={styles.loadMore}>
                            {isFetchingNextPage ? (
                                <span className={styles.loadMoreText}>Cargando...</span>
                            ) : (
                                <button
                                    className={styles.loadMoreBtn}
                                    onClick={() => fetchNextPage()}
                                    type="button"
                                >
                                    Cargar mas
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
